import { useQuery } from "@tanstack/react-query";
import { Alert, Skeleton, Tag } from "antd";

import { Seo } from "@/components/seo/Seo";
import { routePaths } from "@/routes/routePaths";
import { getAdminSettings } from "@/services/adminApi";

function BoolTag({ value }: { value: boolean }) {
  return (
    <Tag color={value ? "green" : "default"} className="m-0 rounded-none">
      {value ? "Enabled" : "Off"}
    </Tag>
  );
}

function SettingRow({ label, value }: { label: string; value: string | number | boolean }) {
  return (
    <div className="grid gap-1 border-b border-neutral-100 py-3 text-sm last:border-b-0 dark:border-neutral-900 sm:grid-cols-[220px_1fr]">
      <span className="text-neutral-500">{label}</span>
      <span className="font-medium text-neutral-900 dark:text-neutral-100">
        {typeof value === "boolean" ? <BoolTag value={value} /> : value || "-"}
      </span>
    </div>
  );
}

export function AdminSettingsPage() {
  const settingsQuery = useQuery({
    queryKey: ["admin", "settings"],
    queryFn: getAdminSettings
  });

  const settings = settingsQuery.data;

  return (
    <>
      <Seo
        path={routePaths.admin.settings}
        title="Admin Settings | VideoTranscriptSaver"
        description="Admin settings for VideoTranscriptSaver."
        robots="noindex,nofollow"
      />
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-semibold">Settings</h1>
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
            Read-only production configuration from Cloudflare bindings and secrets.
          </p>
        </div>

        {settingsQuery.error ? (
          <Alert type="error" showIcon message={settingsQuery.error.message} className="rounded-none" />
        ) : null}

        {settingsQuery.isLoading ? <Skeleton active paragraph={{ rows: 10 }} /> : null}

        {settings ? (
          <div className="grid gap-8 lg:grid-cols-2">
            <section>
              <h2 className="text-xl font-semibold">Limits</h2>
              <div className="mt-3 border border-neutral-200 px-4 dark:border-neutral-800">
                <SettingRow label="Free tool limit" value={settings.limits.freeToolLimit} />
                <SettingRow label="Rate limit window" value={`${settings.limits.rateLimitWindowSeconds} seconds`} />
                <SettingRow label="Max media size" value={`${settings.limits.maxInputSizeMb} MB`} />
                <SettingRow label="Max summary text" value={`${settings.limits.maxSummaryCharacters} characters`} />
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold">Providers</h2>
              <div className="mt-3 border border-neutral-200 px-4 dark:border-neutral-800">
                <SettingRow label="Cloudflare AI" value={settings.providers.cloudflareAi} />
                <SettingRow label="OpenAI fallback" value={settings.providers.openAiTranscriptionFallback} />
                <SettingRow label="Development mock mode" value={settings.providers.mockMode} />
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold">Storage</h2>
              <div className="mt-3 border border-neutral-200 px-4 dark:border-neutral-800">
                <SettingRow label="Audit database" value={settings.storage.auditDatabase} />
                <SettingRow label="Rate limit KV" value={settings.storage.rateLimitKv} />
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold">Admin auth</h2>
              <div className="mt-3 border border-neutral-200 px-4 dark:border-neutral-800">
                <SettingRow label="Auth configured" value={settings.admin.authConfigured} />
                <SettingRow label="Session length" value={`${settings.admin.sessionHours} hours`} />
                <SettingRow label="Password hash env" value={settings.admin.passwordHashVariable} />
                <SettingRow label="Session secret env" value={settings.admin.sessionSecretVariable} />
              </div>
            </section>

            <section className="lg:col-span-2">
              <h2 className="text-xl font-semibold">Environment</h2>
              <div className="mt-3 border border-neutral-200 px-4 dark:border-neutral-800">
                <SettingRow label="Cloudflare environment" value={settings.environment} />
                <SettingRow label="Public site URL" value={settings.publicSiteUrl} />
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </>
  );
}
